import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import { useDeliveryFeesQuery, useSaveDeliveryFeesMutation } from "@queries/website";
import "@styles/opencart-admin.css";

// Flat delivery fee (+ optional free-delivery threshold) per order type —
// everything except Shop, which has its own free-above-X rule on the Shop
// Shipping page. Read live by the website AND the Android app (same /api/v2
// backend), so saving here updates both immediately, and by this admin's
// own "regenerate totals" too.
export default function DeliveryFeesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useDeliveryFeesQuery();
  const saveMutation = useSaveDeliveryFeesMutation();

  const [fees, setFees] = React.useState({});
  const [thresholds, setThresholds] = React.useState({});
  const [savedAt, setSavedAt] = React.useState(null);

  React.useEffect(() => {
    if (data?.fees) {
      const nextFees = {};
      const nextThresholds = {};
      for (const key of Object.keys(data.fees)) {
        nextFees[key] = String(data.fees[key].fee);
        nextThresholds[key] = String(data.fees[key].threshold ?? 0);
      }
      setFees(nextFees);
      setThresholds(nextThresholds);
    }
  }, [data]);

  async function handleSave(e) {
    e.preventDefault();
    const feesPayload = {};
    const thresholdsPayload = {};
    for (const key of Object.keys(fees)) {
      feesPayload[key] = Number(fees[key]);
      thresholdsPayload[key] = Number(thresholds[key] || 0);
    }
    await saveMutation.mutateAsync({ fees: feesPayload, thresholds: thresholdsPayload });
    queryClient.invalidateQueries({ queryKey: ["website", "delivery-fees"] });
    setSavedAt(Date.now());
  }

  if (isLoading) return <Loader />;

  const groups = data?.fees || {};

  return (
    <div className="oc-page">
      <h1>Delivery Fees</h1>
      <p style={{ color: "#666", maxWidth: 680 }}>
        Flat delivery fee charged per order type, with an optional free-
        delivery limit (leave at 0 for &quot;no limit&quot; — the fee always
        applies regardless of order size, which is how every type is set up
        today). This does not affect <strong>Shop</strong> orders (detergent,
        softener, etc.) — those use the Shop Shipping page instead. Applies
        to the website and the mobile app immediately, and to this
        admin&apos;s own &quot;regenerate totals&quot; — no deploy needed.
      </p>

      <form onSubmit={handleSave} className="card" style={{ maxWidth: 640, padding: 20 }}>
        {Object.keys(groups).map((key) => (
          <div
            key={key}
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 16,
              marginTop: 14,
              paddingBottom: 14,
              borderBottom: "1px solid #eee"
            }}
          >
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label>{groups[key].label}</label>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>Fee (JOD)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input"
                    value={fees[key] ?? ""}
                    onChange={(e) => setFees((v) => ({ ...v, [key]: e.target.value }))}
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>
                    Free above (JOD) — 0 = no limit
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input"
                    value={thresholds[key] ?? ""}
                    onChange={(e) => setThresholds((v) => ({ ...v, [key]: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        <p style={{ fontSize: 13, color: "#777", marginTop: 14 }}>
          A fee of 0 means that order type ships free (e.g. the on-site
          service visits default to 0 today). A free-above limit of 0 means
          there is no limit — the fee is always charged regardless of order
          size, which is the current setup for every type.
        </p>

        <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12 }}>
          <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? "Saving..." : "Save"}
          </button>
          {savedAt && !saveMutation.isPending && (
            <span style={{ color: "#2e7d32", fontSize: 13 }}>Saved ✓</span>
          )}
          {saveMutation.isError && (
            <span style={{ color: "#b3261e", fontSize: 13 }}>
              {saveMutation.error?.response?.data?.message || "Failed to save."}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
